/**
 * PLP card swatches — single load (main-collection). Click updates hero, swipe track, title, links.
 */
(() => {
  if (window.__tmrCardSwatchesInit) return;
  window.__tmrCardSwatchesInit = true;

  if (!window.__tmrPreloadUrl) {
    const cache = new Map();
    window.__tmrPreloadUrl = (url) => {
      if (!url) return Promise.resolve();
      if (cache.has(url)) return cache.get(url);
      const p = new Promise((resolve) => {
        const im = new Image();
        im.onload = im.onerror = () => resolve();
        im.src = url;
      });
      cache.set(url, p);
      return p;
    };
  }

  const localePrefixFromUrl = (href) => {
    try {
      const u = new URL(href, window.location.origin);
      const match = u.pathname.match(/^\/([a-z]{2}(?:-[a-z]{2})?)(?:\/|$)/i);
      return match ? '/' + match[1] : '';
    } catch (_) {
      return '';
    }
  };

  const getProductCard = (el) =>
    el?.closest?.('product-card') || el?.closest?.('.product-card') || null;

  const quickAddVariantInfoCache = new Map();
  const fetchProductVariantInfo = (handle) => {
    if (!handle) return Promise.resolve(null);
    if (quickAddVariantInfoCache.has(handle)) return quickAddVariantInfoCache.get(handle);
    const p = (async () => {
      try {
        const lp = localePrefixFromUrl(window.location.href);
        const res = await fetch(`${lp}/products/${handle}.js`);
        if (!res.ok) return null;
        const data = await res.json();
        const variant = data?.variants?.find((v) => v.available) || data?.variants?.[0] || null;
        if (!variant) return null;
        return {
          id: variant.id,
          available: Boolean(variant.available),
          price: typeof variant.price === 'number' ? variant.price : null,
          compareAtPrice: typeof variant.compare_at_price === 'number' ? variant.compare_at_price : null,
        };
      } catch (_) {
        return null;
      }
    })();
    quickAddVariantInfoCache.set(handle, p);
    return p;
  };

  // Shopify's {{amount}} already includes the thousands comma (e.g. "1,210.00");
  // {{amount_with_comma_separator}} is actually the European format (comma decimal,
  // period thousands), not "add a comma" like the name suggests.
  const withThousands = (value, sep) => {
    const parts = value.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, sep);
    return parts.join('.');
  };

  const formatMoneyCents = (cents, format) => {
    const num = parseFloat(cents);
    if (isNaN(num)) return '';
    const fmt = format || window.themeVariables?.settings?.moneyFormat || '${{amount}}';
    const dollars = num / 100;
    const twoDecimals = dollars.toFixed(2);
    const noDecimals = Math.round(dollars).toString();
    const replacements = {
      amount: withThousands(twoDecimals, ','),
      amount_no_decimals: withThousands(noDecimals, ','),
      amount_with_comma_separator: withThousands(twoDecimals, '.').replace(/\.(\d{2})$/, ',$1'),
      amount_no_decimals_with_comma_separator: withThousands(noDecimals, '.'),
      amount_with_apostrophe_separator: withThousands(twoDecimals, "'"),
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, (m, token) => (replacements[token] !== undefined ? replacements[token] : m));
  };

  const moneyFormatFor = () => {
    const settings = window.themeVariables?.settings || {};
    return settings.currencyCodeEnabled ? settings.moneyWithCurrencyFormat : settings.moneyFormat;
  };

  // sale-price/compare-at-price keep a sr-only span before the price text
  // (price-list.liquid) — only the trailing text node can be replaced.
  const setPriceElText = (el, cents) => {
    if (!el) return;
    const formatted = formatMoneyCents(cents, moneyFormatFor());
    if (!formatted) return;
    const textNode = document.createTextNode(formatted);
    if (el.lastChild) el.lastChild.replaceWith(textNode);
    else el.appendChild(textNode);
  };

  // price-list.liquid only renders <compare-at-price> when the card started on sale —
  // switching to a sale variant from a non-sale card has nowhere to put the strikethrough.
  const buildCompareAtPriceEl = (saleEl) => {
    const baseClasses = Array.from(saleEl.classList).filter(
      (c) => c !== 'text-subdued' && c !== 'text-on-sale'
    );
    const el = document.createElement('compare-at-price');
    el.className = [...baseClasses, 'text-subdued', 'line-through'].join(' ');
    const srOnly = document.createElement('span');
    srOnly.className = 'sr-only';
    srOnly.textContent = 'Regular price';
    el.appendChild(srOnly);
    el.appendChild(document.createTextNode(''));
    saleEl.insertAdjacentElement('afterend', el);
    return el;
  };

  const updateCardPriceDisplay = (card, info) => {
    if (!card || !info || typeof info.price !== 'number') return;
    const saleEl = card.querySelector('price-list sale-price');
    if (!saleEl) return;
    let compareEl = card.querySelector('price-list compare-at-price');

    const isOnSale = typeof info.compareAtPrice === 'number' && info.compareAtPrice > info.price;

    setPriceElText(saleEl, info.price);
    saleEl.classList.remove('text-subdued', 'text-on-sale');
    saleEl.classList.add(isOnSale ? 'text-on-sale' : 'text-subdued');

    if (isOnSale) {
      if (!compareEl) compareEl = buildCompareAtPriceEl(saleEl);
      setPriceElText(compareEl, info.compareAtPrice);
      compareEl.removeAttribute('hidden');
    } else if (compareEl) {
      compareEl.setAttribute('hidden', '');
    }
  };

  const syncQuickAddVariant = (card, handle) => {
    if (!card || !handle) return;
    const submitBtn = card.querySelector('.product-card__quick-add-button[type="submit"]');
    const modalBtn = card.querySelector('.product-card__quick-add-button[type="button"]');
    const form = submitBtn?.closest('form');
    const idInput = form?.querySelector('input[name="id"]');
    const btn = submitBtn || modalBtn;
    if (!btn || (submitBtn && (!form || !idInput))) return;

    // Disable immediately so a click during the lookup can't submit the
    // previous swatch's variant id (or open the modal for it).
    btn.disabled = true;

    fetchProductVariantInfo(handle).then((info) => {
      // Bail if the shopper picked a different swatch while this was in flight.
      if (card.getAttribute('handle') !== handle) return;
      if (!info) {
        btn.disabled = true;
        return;
      }
      if (idInput) idInput.value = info.id;
      btn.disabled = !info.available;
    });
  };

  const bumpFigHover = (figure) => {
    if (!figure) return 0;
    figure._tmrHoverGen = (figure._tmrHoverGen | 0) + 1;
    return figure._tmrHoverGen;
  };

  const getVariantUrlsFromBtn = (btn) => {
    const preview = (btn.getAttribute('data-preview') || '').trim();
    const raw = (btn.getAttribute('data-media') || '').trim();
    const thumb = (btn.getAttribute('data-thumb') || getSwatchThumbUrl(btn) || '').trim();

    if (raw) {
      const urls = raw
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      if (preview && !urls.includes(preview)) urls.unshift(preview);
      return urls.slice(0, 8);
    }
    if (preview) return [preview];
    if (thumb) return [thumb];
    return [];
  };

  const getSwatchThumbUrl = (btn) => {
    const thumb = btn?.getAttribute?.('data-thumb') || '';
    if (thumb) return thumb;
    return btn?.querySelector?.('.tmr-card-swatch__img')?.currentSrc || btn?.querySelector?.('.tmr-card-swatch__img')?.src || '';
  };

  const preloadUrl = (url) => window.__tmrPreloadUrl?.(url) ?? Promise.resolve();

  const warmSwatchUrls = (btn) => {
    if (!btn) return;
    getVariantUrlsFromBtn(btn).forEach((u) => preloadUrl(u));
    const thumb = getSwatchThumbUrl(btn);
    const urls = getVariantUrlsFromBtn(btn);
    if (thumb && !urls.includes(thumb)) preloadUrl(thumb);
  };

  const tmrImageKey = (url) => {
    if (!url) return '';
    try {
      return new URL(url, window.location.origin).pathname;
    } catch (_) {
      return String(url).split('?')[0];
    }
  };

  const setImgSrcFast = (img, url, force = false) => {
    if (!img || !url) return;
    const cur = img.currentSrc || img.src || '';
    if (!force && cur && tmrImageKey(cur) === tmrImageKey(url)) return;
    try {
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      img.loading = 'eager';
      img.decoding = 'async';
      img.src = url;
      img.classList.add('tmr-img-ready');
    } catch (_) {}
    void preloadUrl(url);
  };

  const swapPrimaryImage = (card, url) => {
    const media = card?.querySelector?.('.product-card__media');
    if (!media || !url) return;
    const primary = media.querySelector('img.product-card__image--primary');
    if (!primary) return;

    if (tmrImageKey(primary.currentSrc || primary.src || '') === tmrImageKey(url)) {
      setImgSrcFast(primary, url, true);
      return;
    }

    const next = document.createElement('img');
    next.className = primary.className;
    if (primary.alt) next.alt = primary.alt;
    if (primary.width) next.width = primary.width;
    if (primary.height) next.height = primary.height;
    next.loading = 'eager';
    next.decoding = 'async';
    next.src = url;
    next.classList.add('tmr-img-ready');
    primary.replaceWith(next);
    void preloadUrl(url);
  };

  const updateSwipeImages = (card, imageUrls, force = false) => {
    if (!card || !imageUrls?.length) return;
    const track = card.querySelector('.product-card__swipe-track');
    if (!track) return;

    const slides = Array.from(track.children).filter((el) => el.dataset.clone !== '1');
    slides.forEach((slide, i) => {
      const url = imageUrls[i];
      const img = slide.querySelector('img');
      if (url && img) setImgSrcFast(img, url, force);
    });
    const clones = Array.from(track.children).filter((el) => el.dataset.clone === '1');
    if (clones.length >= 2 && imageUrls.length) {
      const first = clones[0]?.querySelector('img');
      const last = clones[clones.length - 1]?.querySelector('img');
      if (first) setImgSrcFast(first, imageUrls[imageUrls.length - 1], force);
      if (last) setImgSrcFast(last, imageUrls[0], force);
    }

    const dots = Array.from(card.querySelectorAll('.product-card__swipe .product-card__dot'));
    if (dots.length) {
      dots.forEach((d, idx) => d.classList.toggle('product-card__dot--active', idx === 0));
    }
  };

  const updateStaticCardMedia = (card, imageUrls, force = false) => {
    if (!card || !imageUrls?.length) return;

    const primaryUrl = imageUrls[0];
    const secondaryUrl = imageUrls[1] || '';
    const figure = card.querySelector('.product-card__figure');

    swapPrimaryImage(card, primaryUrl);

    const secondary = card.querySelector('.product-card__media img.product-card__image--secondary');
    if (secondary) {
      if (!secondaryUrl) {
        try {
          secondary.removeAttribute('src');
          secondary.removeAttribute('srcset');
          secondary.removeAttribute('sizes');
        } catch (_) {}
        figure?.classList.add('tmr-card--no-hover-secondary');
      } else {
        figure?.classList.remove('tmr-card--no-hover-secondary');
        setImgSrcFast(secondary, secondaryUrl, force);
      }
    }
  };

  const syncPlpCardVariantImages = (card, imageUrls, force = false) => {
    if (!card || !imageUrls?.length) return;
    updateStaticCardMedia(card, imageUrls, force);
    updateSwipeImages(card, imageUrls, force);
  };

  const getSoldOutLabel = (card) => {
    const fromSwatches = card.querySelector('[data-tmr-card-swatches]')?.dataset?.soldOutLabel;
    return (fromSwatches || 'Sold out').trim() || 'Sold out';
  };

  const ensureSoldOutBadge = (card) => {
    const figure = card?.querySelector('.product-card__figure');
    if (!figure) return null;

    let badge = card.querySelector('sold-out-badge.badge--sold-out');
    if (badge) return badge;

    let badgeList = figure.querySelector('badge-list');
    if (!badgeList) {
      badgeList = document.createElement('badge-list');
      badgeList.className = 'badge-list badge-list--vertical';
      figure.insertBefore(badgeList, figure.firstChild);
    }

    badge = document.createElement('sold-out-badge');
    badge.className = 'badge badge--sold-out';
    badge.textContent = getSoldOutLabel(card);
    badgeList.appendChild(badge);
    return badge;
  };

  const updateCardSoldOutState = (card, isAvailable) => {
    if (!card) return;
    const ok = Boolean(isAvailable);
    try {
      card.setAttribute('data-tmr-available', ok ? 'true' : 'false');
    } catch (_) {}

    const badgeList = card.querySelector('.product-card__figure badge-list');
    let soldOutBadge = card.querySelector('sold-out-badge.badge--sold-out');

    if (ok) {
      if (soldOutBadge) soldOutBadge.setAttribute('hidden', '');
      return;
    }

    soldOutBadge = ensureSoldOutBadge(card);
    if (!soldOutBadge) return;

    soldOutBadge.removeAttribute('hidden');
    if (badgeList) badgeList.removeAttribute('hidden');
    try {
      badgeList.hidden = false;
    } catch (_) {}
  };

  const updateCardTitle = (card, title) => {
    if (!card || !title) return;
    const t = card.querySelector('.product-card__info a.product-title, .product-card__info .product-title');
    if (!t) return;
    try {
      t.textContent = title;
    } catch (_) {}
  };

  const setCardToHandle = (card, handle) => {
    if (!card || !handle) return;
    try {
      card.setAttribute('handle', handle);
    } catch (_) {}
    const allLinks = Array.from(card.querySelectorAll('a[href*="/products/"]'));
    for (const a of allLinks) {
      try {
        const lp = localePrefixFromUrl(a.getAttribute('href'));
        a.setAttribute('href', `${lp}/products/${handle}`);
      } catch (_) {}
    }

    const quickBuyModal = card.querySelector('quick-buy-modal');
    if (quickBuyModal) {
      try {
        quickBuyModal.setAttribute('handle', handle);
      } catch (_) {}
    }
  };

  const applyVariantSelection = (btn, swatchRoot, card) => {
    if (!btn || !swatchRoot || !card) return;

    const nextHandle = btn.getAttribute('data-handle') || btn.dataset.handle;
    if (!nextHandle) return;

    swatchRoot.querySelectorAll('.tmr-card-swatch').forEach((b) => {
      const isSelected = b === btn;
      b.classList.toggle('is-selected', isSelected);
      if (isSelected) {
        b.setAttribute('aria-current', 'true');
      } else {
        b.removeAttribute('aria-current');
      }
    });
    setCardToHandle(card, nextHandle);
    syncQuickAddVariant(card, nextHandle);
    updateCardTitle(card, btn.getAttribute('data-title') || '');
    updateCardSoldOutState(card, btn.getAttribute('data-available') === '1');

    // Storefront-API swatches already carry their price (renderApiSwatchRow) — paint
    // it instantly instead of waiting on a fetch. Pool-fallback swatches don't have
    // this, so they just fall through to the fetch below.
    const instantPrice = btn.getAttribute('data-price');
    if (instantPrice !== null) {
      const instantCompare = btn.getAttribute('data-compare-price');
      updateCardPriceDisplay(card, {
        price: parseInt(instantPrice, 10),
        compareAtPrice: instantCompare !== null ? parseInt(instantCompare, 10) : null,
      });
    }

    // Still confirm against the REST endpoint, same safety net as quick-add,
    // in case the GraphQL price field didn't resolve.
    fetchProductVariantInfo(nextHandle).then((info) => {
      // Bail if the shopper picked a different swatch while this was in flight.
      if (card.getAttribute('handle') !== nextHandle) return;
      updateCardPriceDisplay(card, info);
    });

    const urls = getVariantUrlsFromBtn(btn);
    if (!urls.length) return;

    const fig = card.querySelector('.product-card__figure');

    try {
      fig?.dispatchEvent(new CustomEvent('tmr:plp-variant-change', { bubbles: false }));
    } catch (_) {}
    bumpFigHover(fig);
    try {
      fig?.classList.remove('tmr-plp-swipe-hover-ready');
      fig?.classList.add('tmr-plp-swatch-changed');
    } catch (_) {}

    syncPlpCardVariantImages(card, urls, true);
    urls.forEach((u) => preloadUrl(u));

    const swipe = card.querySelector('.product-card__swipe');
    const track = swipe?.querySelector('.product-card__swipe-track');
    const dotsWrap = swipe?.querySelector('.product-card__dots');

    if (swipe && track && urls.length >= 1) {
      const prevH = swipe.getBoundingClientRect().height;
      if (prevH > 0) swipe.style.minHeight = `${prevH}px`;

      const existingImg = track.querySelector('img');
      const cls = existingImg?.getAttribute('class') || 'product-card__image product-card__image--primary';
      const wAttr = existingImg?.getAttribute('width') || '';
      const hAttr = existingImg?.getAttribute('height') || '';

      const href =
        card.querySelector('a.product-title')?.getAttribute('href') ||
        card.querySelector('.product-card__media')?.getAttribute('data-tmr-card-href') ||
        `${localePrefixFromUrl(window.location.href)}/products/${nextHandle}`;

      const slidesHtml = urls
        .map((u, idx) => {
          const safe = String(u).replace(/"/g, '&quot;');
          const wh = wAttr && hAttr ? ` width="${wAttr}" height="${hAttr}"` : '';
          const fp = idx < 5 ? ' fetchpriority="high"' : ' fetchpriority="low"';
          return `<div class="product-card__swipe-slide" data-tmr-card-href="${href}" aria-hidden="true"><img class="${cls}" loading="eager" decoding="async"${fp}${wh} src="${safe}" alt=""></div>`;
        })
        .join('');

      const oldPrev = swipe.querySelector('.product-card__swipe-arrow--prev');
      const oldNext = swipe.querySelector('.product-card__swipe-arrow--next');
      if (oldPrev) {
        const freshPrev = oldPrev.cloneNode(true);
        delete freshPrev.dataset.tmrSwipeArrow;
        oldPrev.replaceWith(freshPrev);
      }
      if (oldNext) {
        const freshNext = oldNext.cloneNode(true);
        delete freshNext.dataset.tmrSwipeArrow;
        oldNext.replaceWith(freshNext);
      }

      try {
        delete swipe.dataset.tmrDesktopInit;
      } catch (_) {}

      if (dotsWrap) {
        const newDots = dotsWrap.cloneNode(false);
        newDots.innerHTML = urls
          .map((_, i) => `<span class="product-card__dot ${i === 0 ? 'product-card__dot--active' : ''}"></span>`)
          .join('');
        dotsWrap.replaceWith(newDots);
        window.__tmrSyncPlpSwipeDots?.(swipe);
      }

      const newTrack = track.cloneNode(false);
      delete newTrack.dataset.tmrSwipe;
      newTrack.removeAttribute('style');
      newTrack.innerHTML = slidesHtml;
      track.replaceWith(newTrack);
      Array.from(newTrack.querySelectorAll('img')).forEach((img) => {
        try {
          img.classList.add('tmr-img-ready');
        } catch (_) {}
      });
      try {
        fig?.removeAttribute('data-tmr-swipe-view-primed');
      } catch (_) {}
      window.__tmrInitPlpSwipeTrack?.(newTrack);
      void window.__tmrPrimeSwipeTrackImages?.(newTrack);
      syncPlpCardVariantImages(card, urls, true);

      try {
        swipe.setAttribute('data-swipe-count', String(urls.length));
      } catch (_) {}

      if (urls.length < 2) {
        try {
          fig?.classList.add('tmr-card--single-plp-image');
        } catch (_) {}
      } else {
        try {
          fig?.classList.remove('tmr-card--single-plp-image');
        } catch (_) {}
      }

      const first = newTrack.querySelector('img');
      if (first) {
        first.addEventListener('load', () => {
          swipe.style.minHeight = '';
        }, { once: true });
      } else {
        swipe.style.minHeight = '';
      }
    }
  };

  const PLP_SWATCH_MAX_VISIBLE = 3;

  const getShapeAliases = (root) => {
    const fromAttr = (root?.dataset?.shapeAliases || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    if (fromAttr.length) return fromAttr;
    const tag = root?.dataset?.shapeTag?.trim();
    return tag ? [tag] : [];
  };

  const tagMatchesShape = (tags, aliases) => {
    if (!Array.isArray(tags)) return false;
    const lowered = tags.map((t) => String(t).toLowerCase());
    return aliases.some((alias) => lowered.includes(String(alias).toLowerCase()));
  };

  const markSwatchesReady = (root) => {
    if (!root) return;
    root.classList.add('tmr-card-swatches--ready');
  };

  const finalizeSwatchRoot = (root) => {
    if (!root) return;
    applySwatchVisibility(root);
    delete root.dataset.tmrWarmBound;
    bindCardSwatches(root);
  };

  const getMediaUrlsFromNode = (node) => {
    const urls = [];
    const add = (url) => {
      const u = String(url || '').trim();
      if (!u || urls.includes(u)) return;
      urls.push(u);
    };
    add(node?.featuredImage?.url);
    node?.media?.edges?.forEach(({ node: m }) => add(m?.image?.url));
    add(node?.flowerImage?.value);
    return urls.slice(0, 8);
  };

  const escapeAttr = (value) => String(value || '').replace(/"/g, '&quot;');

  const applySwatchVisibility = (root) => {
    if (!root) return;
    const row = root.querySelector('.tmr-card-swatches__row');
    if (!row) return;

    const buttons = [...row.querySelectorAll('.tmr-card-swatch')];
    const existingMore = row.querySelector('.tmr-card-swatch-more');

    if (root.dataset.tmrSwatchesExpanded === '1') {
      if (existingMore) existingMore.remove();
      if (buttons.some((b) => b.classList.contains('is-hidden'))) {
        buttons.forEach((b) => b.classList.remove('is-hidden'));
      }
      return;
    }

    if (buttons.length <= PLP_SWATCH_MAX_VISIBLE) {
      if (existingMore) existingMore.remove();
      if (buttons.some((b) => b.classList.contains('is-hidden'))) {
        buttons.forEach((b) => b.classList.remove('is-hidden'));
      }
      return;
    }

    const visible = new Set(
      [0, 1, 2].filter((i) => i < buttons.length).map((i) => buttons[i])
    );
    const selectedIdx = buttons.findIndex((b) => b.classList.contains('is-selected'));
    if (selectedIdx >= PLP_SWATCH_MAX_VISIBLE && buttons[selectedIdx]) {
      const visibleArr = [...visible];
      visible.delete(visibleArr[visibleArr.length - 1]);
      visible.add(buttons[selectedIdx]);
    }

    const hiddenCount = buttons.length - visible.size;
    const expectedLabel = `+${hiddenCount}`;
    let dirty = false;

    buttons.forEach((b) => {
      const hide = !visible.has(b);
      if (b.classList.contains('is-hidden') !== hide) dirty = true;
    });

    if (hiddenCount > 0) {
      if (!existingMore || existingMore.textContent !== expectedLabel) dirty = true;
    } else if (existingMore) {
      dirty = true;
    }

    if (!dirty) return;

    buttons.forEach((b) => b.classList.toggle('is-hidden', !visible.has(b)));

    if (hiddenCount > 0) {
      if (existingMore) {
        existingMore.textContent = expectedLabel;
        existingMore.setAttribute('aria-label', `Show ${hiddenCount} more color options`);
      } else {
        const more = document.createElement('button');
        more.type = 'button';
        more.className = 'tmr-card-swatch-more';
        more.setAttribute('aria-label', `Show ${hiddenCount} more color options`);
        more.textContent = expectedLabel;
        row.appendChild(more);
      }
    } else if (existingMore) {
      existingMore.remove();
    }
  };

  const bindCardSwatches = (el) => {
    if (!el || el.dataset.tmrWarmBound) return;
    el.dataset.tmrWarmBound = '1';
    // No preload here: warming every swatch of every card on page load downloaded hundreds of
    // photos per collection page. Swatch photos load on hover (warmSwatchUrls) or on tap.
  };

  const stopPlpNav = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
  };

  const handlePlpSwatchInteraction = (e) => {
    const root = e.target.closest?.('[data-tmr-card-swatches]');
    if (!root) return;

    const card = getProductCard(root);
    if (!card) return;

    const reveal = e.target.closest?.('.tmr-card-swatch-more');
    if (reveal && root.contains(reveal)) {
      stopPlpNav(e);
      root.dataset.tmrSwatchesExpanded = '1';
      try {
        card.querySelector('.product-card__figure')?.dispatchEvent(
          new CustomEvent('tmr:swatches-expand', { bubbles: false })
        );
      } catch (_) {}
      root.querySelectorAll('.tmr-card-swatch.is-hidden').forEach((b) => b.classList.remove('is-hidden'));
      reveal.remove();
      return;
    }

    const btn = e.target.closest?.('.tmr-card-swatch');
    if (!btn || !root.contains(btn) || btn.classList.contains('is-hidden')) return;

    stopPlpNav(e);
    applyVariantSelection(btn, root, card);
  };

  const installPlpSwatchDelegates = () => {
    if (window.__tmrPlpSwatchDelegatesInstalled) return;
    window.__tmrPlpSwatchDelegatesInstalled = true;
    document.addEventListener('click', handlePlpSwatchInteraction, true);
    document.addEventListener(
      'keydown',
      (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        if (!e.target.closest?.('.tmr-card-swatch, .tmr-card-swatch-more')) return;
        handlePlpSwatchInteraction(e);
      },
      true
    );
  };

  const isColorRosePlp = () => Boolean(document.querySelector('[data-tmr-color-rose-plp="1"]'));

  const isLongStemProductCard = (card) => {
    if (!card) return false;
    const handle = String(card.getAttribute('handle') || '').toLowerCase();
    const title = String(
      card.querySelector('.product-title')?.textContent || ''
    ).toLowerCase();
    return (
      /long[\s_-]?stem/.test(handle) ||
      /long[\s_-]?stem/.test(title) ||
      /long[\s_-]?stem\s+roses/.test(title)
    );
  };

  const isAcrylicDrawerProductCard = (card) => {
    if (!card) return false;
    const handle = String(card.getAttribute('handle') || '').toLowerCase();
    const title = String(
      card.querySelector('.product-title')?.textContent || ''
    ).toLowerCase();
    return handle.includes('acrylic') || title.includes('acrylic');
  };

  const stripColorRoseExcludedSwatches = () => {
    if (!isColorRosePlp()) return;
    document.querySelectorAll('product-card').forEach((card) => {
      if (!isLongStemProductCard(card) && !isAcrylicDrawerProductCard(card)) return;
      card.querySelector('[data-tmr-card-swatches]')?.remove();
      card.querySelector('.product-card__info fieldset[data-option-position]')?.remove();
      card.querySelector('.product-card__info .smallcaps.text-subdued')?.remove();
    });
  };

  const disableQuickAddForSoldOutCards = () => {
    document.querySelectorAll('product-card, .product-card').forEach((card) => {
      if (card.getAttribute('data-tmr-available') !== 'false') return;
      const btn = card.querySelector('.product-card__quick-add-button');
      if (btn) btn.disabled = true;
    });
  };

  const bootCardSwatches = () => {
    stripColorRoseExcludedSwatches();
    document.querySelectorAll('[data-tmr-card-swatches]').forEach((root) => {
      markSwatchesReady(root);
      finalizeSwatchRoot(root);
    });
    disableQuickAddForSoldOutCards();
  };

  const plpTagFetchCache = window.__tmrPlpTagCache || new Map();
  window.__tmrPlpTagCache = plpTagFetchCache;
  const plpTagResolved = window.__tmrPlpTagResolved || (window.__tmrPlpTagResolved = Object.create(null));
  const PLP_TAG_STORAGE_KEY = 'tmr_plp_swatch_tag_cache_v14';
  const PLP_TAG_STORAGE_TTL_MS = 15 * 60 * 1000;

  const tagNodesComplete = (nodes) => Boolean(Array.isArray(nodes) && nodes.__tmrPageComplete);

  const markTagNodesComplete = (nodes) => {
    if (Array.isArray(nodes)) nodes.__tmrPageComplete = true;
    return nodes;
  };

  const loadStoredTagNodes = (shapeTag) => {
    try {
      const raw = sessionStorage.getItem(PLP_TAG_STORAGE_KEY);
      if (!raw) return null;
      const bag = JSON.parse(raw);
      const entry = bag?.[shapeTag];
      if (!entry?.nodes || !entry?.complete || Date.now() - (entry.at || 0) > PLP_TAG_STORAGE_TTL_MS) {
        return null;
      }
      return markTagNodesComplete(entry.nodes);
    } catch (_) {
      return null;
    }
  };

  const storeTagNodes = (shapeTag, nodes) => {
    if (!shapeTag || !Array.isArray(nodes)) return;
    markTagNodesComplete(nodes);
    plpTagResolved[shapeTag] = nodes;
    try {
      const bag = JSON.parse(sessionStorage.getItem(PLP_TAG_STORAGE_KEY) || '{}');
      bag[shapeTag] = { at: Date.now(), complete: true, nodes };
      sessionStorage.setItem(PLP_TAG_STORAGE_KEY, JSON.stringify(bag));
    } catch (_) {}
  };

  const getPendingPlpSwatchRoots = () =>
    [
      ...document.querySelectorAll(
        '[data-tmr-card-swatches][data-plp-api-swatches="1"]:not([data-tmr-api-enhanced="1"]):not([data-tmr-api-pending="1"])'
      ),
    ].filter((root) => {
      if (!normField(root.dataset.shapeTag)) return false;
      if (
        isColorRosePlp() &&
        (root.dataset.longStemProduct === '1' ||
          isLongStemProductCard(root.closest('product-card')))
      ) {
        return false;
      }
      return true;
    });

  const collectShapeTagsForRoots = (roots) => {
    const tags = new Set();
    roots.forEach((root) => getShapeAliases(root).forEach((t) => tags.add(t)));
    return tags;
  };

  const fetchProductsByShapeTag = async (shapeTag) => {
    if (!shapeTag || !window.TMR_STOREFRONT_TOKEN || !window.TMR_STOREFRONT_API) return [];
    if (tagNodesComplete(plpTagResolved[shapeTag])) return plpTagResolved[shapeTag];
    const stored = loadStoredTagNodes(shapeTag);
    if (stored) {
      plpTagResolved[shapeTag] = stored;
      return stored;
    }
    if (plpTagFetchCache.has(shapeTag)) {
      const cached = await plpTagFetchCache.get(shapeTag);
      if (tagNodesComplete(cached)) {
        storeTagNodes(shapeTag, cached);
        return cached;
      }
      plpTagFetchCache.delete(shapeTag);
    }

    const nodeFields = `
      handle
      title
      availableForSale
      # 1200px covers the card on every screen incl. large retina; plain url returns the full-size original (up to 4096px).
      featuredImage { url(transform: { maxWidth: 1200 }) }
      media(first: 8) {
        edges {
          node {
            ... on MediaImage {
              image { url(transform: { maxWidth: 1200 }) }
            }
          }
        }
      }
      flowerImage: metafield(namespace: "custom", key: "stc_flower_image") { value }
      boxImage: metafield(namespace: "custom", key: "stc_box_image") { value }
      flowerName: metafield(namespace: "custom", key: "stc_flower_name") { value }
      boxColor: metafield(namespace: "custom", key: "stc_box_color") { value }
      tags
      priceRange { minVariantPrice { amount } }
      compareAtPriceRange { minVariantPrice { amount } }
    `;

    // Same query as snippets/tmr-plp-swatch-prefetch.liquid (window.__tmrPlpSwatchQuery) — keep both in sync.
    const query = window.__tmrPlpSwatchQuery || `
      query PlpSwatches($tag: String!, $cursor: String) {
        products(first: 250, after: $cursor, query: $tag) {
          pageInfo { hasNextPage endCursor }
          edges {
            node { ${nodeFields} }
          }
        }
      }
    `;

    const p = (async () => {
      const nodes = [];
      let cursor = null;
      let hasNext = true;
      try {
        while (hasNext) {
          const response = await fetch(window.TMR_STOREFRONT_API, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Shopify-Storefront-Access-Token': window.TMR_STOREFRONT_TOKEN,
            },
            body: JSON.stringify({
              query,
              variables: { tag: `tag:${shapeTag}`, cursor },
            }),
          });
          if (!response.ok) break;
          const json = await response.json();
          const page = json?.data?.products;
          if (!page) break;
          nodes.push(...(page.edges?.map((e) => e.node) || []));
          hasNext = Boolean(page.pageInfo?.hasNextPage);
          cursor = page.pageInfo?.endCursor || null;
          if (!hasNext || !cursor) break;
        }
      } catch (_) {
        /* keep partial nodes */
      }
      storeTagNodes(shapeTag, nodes);
      return nodes;
    })();

    plpTagFetchCache.set(shapeTag, p);
    return p;
  };

  const paintPlpSwatchRootsFromNodes = (pending, nodesByHandle) => {
    const groups = new Map();
    pending.forEach((root) => {
      const aliases = getShapeAliases(root);
      if (!aliases.length) {
        root.dataset.tmrApiEnhanced = '1';
        finalizeSwatchRoot(root);
        return;
      }
      const key = aliases.join('\0');
      if (!groups.has(key)) groups.set(key, { aliases, roots: [] });
      groups.get(key).roots.push(root);
    });

    for (const { aliases, roots } of groups.values()) {
      const byHandle = new Map(nodesByHandle);
      for (const tag of aliases) {
        const nodes = plpTagResolved[tag];
        if (!Array.isArray(nodes)) continue;
        nodes.forEach((n) => {
          if (n?.handle) byHandle.set(n.handle, n);
        });
      }
      const nodes = [...byHandle.values()];

      for (const root of roots) {
        if (root.dataset.tmrApiEnhanced === '1') continue;
        paintSwatchRootFromApiOrPool(root, nodes);
      }
    }
  };

  const getPoolFallbackSwatchCount = (root) => {
    const tpl = root.querySelector('template[data-tmr-pool-fallback]');
    if (!tpl?.content) return 0;
    return tpl.content.querySelectorAll('.tmr-card-swatch').length;
  };

  const applyPoolFallbackSwatches = (root) => {
    const tpl = root.querySelector('template[data-tmr-pool-fallback]');
    const row = root.querySelector('.tmr-card-swatches__row');
    if (!tpl?.content || !row) return false;
    const html = tpl.innerHTML.trim();
    if (!html) return false;
    row.innerHTML = html;
    root.classList.add('tmr-card-swatches--ready');
    root.dataset.tmrApiEnhanced = '1';
    markSwatchesReady(root);
    finalizeSwatchRoot(root);
    return true;
  };

  const isColorRoseSwatchRoot = (root) => root?.dataset?.colorRoseCollection === '1';

  const paintSwatchRootFromApiOrPool = (root, nodes) => {
    const variants = buildVariantsForCard(nodes, root);
    const poolCount = getPoolFallbackSwatchCount(root);
    const usesApi = root.dataset.plpApiSwatches === '1';

    if (usesApi && variants.length > 0) {
      renderApiSwatchRow(root, variants);
      return;
    }
    if (poolCount > variants.length && applyPoolFallbackSwatches(root)) return;
    if (variants.length > 0) {
      renderApiSwatchRow(root, variants);
      return;
    }
    if (poolCount > 0 && applyPoolFallbackSwatches(root)) return;
    root.dataset.tmrApiEnhanced = '1';
    finalizeSwatchRoot(root);
  };

  const buildVariantsForCard = (nodes, root) =>
    isColorRoseSwatchRoot(root) || root.dataset.swatchMode === 'box'
      ? buildBoxVariantsForCard(nodes, root)
      : buildFlowerVariantsForCard(nodes, root);

  const normField = (s) => String(s || '').trim();

  const flowerNameKey = (name) => {
    let key = normField(name)
      .toLowerCase()
      .replace(/\s+roses?\s*$/i, '')
      .trim();
    if (key.includes('24k') || key.includes('gold')) return 'gold';
    if (key.includes('bright purple')) return 'bright purple';
    if (key.includes('light pink') || key.includes('light-pink')) return 'light pink';
    if (key.includes('purple')) return 'purple';
    if (key.includes('pink')) return 'pink';
    if (key.includes('red')) return 'red';
    if (key.includes('white')) return 'white';
    if (key.includes('black')) return 'black';
    if (key.includes('blue')) return 'blue';
    if (key.includes('yellow')) return 'yellow';
    if (key.includes('peach')) return 'peach';
    if (key.includes('lavender')) return 'lavender';
    if (key.includes('orange')) return 'orange';
    if (key.includes('green')) return 'green';
    return key;
  };

  const flowerNamesMatch = (a, b) => flowerNameKey(a) === flowerNameKey(b);

  const fieldExactKey = (value) => normField(value).toLowerCase();

  const flowerNamesExact = (a, b) => fieldExactKey(a) === fieldExactKey(b);

  const boxColorsExact = (a, b) => fieldExactKey(a) === fieldExactKey(b);

  const isLongStemShapeRoot = (root) => {
    if (root.dataset.longStemProduct === '1' || root.dataset.longStemCollection === '1') return true;
    return getShapeAliases(root).some((t) => t.includes('long_stem') || t === 'longstem');
  };

  const boxColorKey = (value) => normField(value).toLowerCase();

  const titleLeadKey = (title) =>
    String(title || '')
      .split('|')[0]
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');

  const titleHasCrystalEdition = (title) =>
    String(title || '').toLowerCase().includes('crystal edition');

  const nodeMatchesEdition = (node, root) => {
    const cardCrystal = titleHasCrystalEdition(root.dataset.productTitle || root.dataset.titleLead || '');
    const nodeCrystal = titleHasCrystalEdition(node?.title || '');
    return cardCrystal === nodeCrystal;
  };

  const inferBoxColorFromTitle = (title) => {
    const lead = titleLeadKey(title);
    if (lead.includes('white') && (lead.includes('box') || lead.includes('dome'))) return 'White';
    if (lead.includes('black') && (lead.includes('box') || lead.includes('dome'))) return 'Black';
    if (lead.includes('gold') && (lead.includes('box') || lead.includes('dome'))) return 'Gold';
    if (lead.includes('red') && lead.includes('box')) return 'Red';
    if (lead.includes('white box')) return 'White';
    if (lead.includes('black box')) return 'Black';
    if (lead.includes('gold box')) return 'Gold';
    if (lead.includes('red box')) return 'Red';
    if (lead.includes('burgundy') || lead.includes('bordeaux')) return 'Bordeaux';
    if (lead.includes('beige')) return 'Beige';
    if (lead.includes('white')) return 'White';
    if (lead.includes('black')) return 'Black';
    if (lead.includes('gold')) return 'Gold';
    if (lead.includes('red')) return 'Red';
    return '';
  };

  const canonicalBoxColorKey = (value, title) => {
    const raw = boxColorKey(value || inferBoxColorFromTitle(title));
    if (!raw) return '';
    if (raw.includes('burgundy') || raw.includes('bordeaux')) return 'burgundy';
    if (raw.includes('beige')) return 'beige';
    if (raw.includes('black')) return 'black';
    if (raw.includes('white')) return 'white';
    if (raw.includes('red')) return 'red';
    if (raw.includes('gold')) return 'gold';
    if (raw.includes('pink')) return 'pink';
    if (raw.includes('navy')) return 'navy';
    if (raw.includes('royal blue') || raw.includes('royal-blue')) return 'royal-blue';
    return raw;
  };

  const nodeBoxValue = (node) =>
    normField(node?.boxColor?.value) || inferBoxColorFromTitle(node?.title);

  const nodeMatchesShapeTag = (node, root) => {
    const tags = node?.tags;
    if (!Array.isArray(tags)) return false;
    const primary = normField(root?.dataset?.shapeTag);
    if (primary && tags.includes(primary)) return true;
    return tagMatchesShape(tags, getShapeAliases(root));
  };

  const nodeMatchesShapeFamily = (node, root) => {
    const family = normField(root.dataset.shapeFamily || '').toLowerCase();
    if (!family || family === 'other' || family === 'longstem' || family === 'sunflower' || family === 'buttercup') {
      return true;
    }
    const lead = titleLeadKey(node?.title);
    const cardLead = titleLeadKey(root.dataset.titleLead || '');
    const isBoxMode = isColorRoseSwatchRoot(root) || root.dataset.swatchMode === 'box';
    const cardIsDome = cardLead.includes('dome');
    const nodeIsDome = lead.includes('dome');

    if (family === 'supreme') {
      if (!lead.includes('supreme')) return false;
      const cardSuperdome = cardLead.includes('superdome');
      const nodeSuperdome = lead.includes('superdome');
      return cardSuperdome === nodeSuperdome;
    }
    if (family === 'babyheart') {
      return lead.includes('baby heart') || lead.includes('babyheart');
    }
    if (family === 'heart') {
      if (lead.includes('baby heart') || lead.includes('babyheart')) return false;
      return lead.includes('heart');
    }
    if (family === 'deluxe') {
      if (!lead.includes('deluxe')) return false;
      if (lead.includes('classic') || lead.includes('supreme')) return false;
      if (cardIsDome !== nodeIsDome) return false;
      // Box swatches: group all box colors for the same flower (Black/White/Gold).
      if (isBoxMode) return true;
      if (cardLead.includes('black box') || (cardLead.includes('black') && cardLead.includes('deluxe'))) {
        return lead.includes('black box') || (lead.includes('deluxe') && lead.includes('black'));
      }
      if (cardLead.includes('white box') || (cardLead.includes('white') && cardLead.includes('deluxe'))) {
        return lead.includes('white box') || (lead.includes('deluxe') && lead.includes('white'));
      }
      return lead.includes('deluxe');
    }
    if (family === 'classic') {
      if (!lead.includes('classic')) return false;
      if (lead.includes('deluxe') || lead.includes('supreme')) return false;
      const cardCrystal = titleHasCrystalEdition(root.dataset.productTitle || '');
      const nodeCrystal = titleHasCrystalEdition(node?.title || '');
      if (cardCrystal !== nodeCrystal) return false;
      if (isBoxMode) {
        if (cardIsDome !== nodeIsDome) return false;
        return true;
      }
      return true;
    }
    if (family === 'dome') return lead.includes('dome') && !lead.includes('superdome');
    if (family === 'superdome') return lead.includes('superdome');
    if (family === 'hexagon') return lead.includes('hexagon');
    if (family === 'essence') {
      return lead.includes('essence') && !lead.includes('superdome');
    }
    if (family === 'suede') {
      if (lead.includes('superdome')) return false;
      const cardTitle = String(root.dataset.productTitle || '').toLowerCase();
      if (cardTitle.includes('classic')) {
        if (!lead.includes('classic')) return false;
        if (lead.includes('deluxe') || lead.includes('supreme')) return false;
        const cardCrystal = titleHasCrystalEdition(root.dataset.productTitle || '');
        const nodeCrystal = titleHasCrystalEdition(node?.title || '');
        if (cardCrystal !== nodeCrystal) return false;
        return true;
      }
      return lead.includes('suede');
    }
    return true;
  };

  /** PDP colorSwatches rules + dome/deluxe title fallback. */
  const boxColorMatchPdp = (cardBox, nodeBox, nodeTitle, cardTitleLead) => {
    const card = normField(cardBox);
    const node = normField(nodeBox) || inferBoxColorFromTitle(nodeTitle);
    if (!card || card === '__any__') return true;
    if (!node) return false;
    if (canonicalBoxColorKey(card) === canonicalBoxColorKey(node)) return true;
    if (boxColorKey(card) === boxColorKey(node)) return true;
    const lead = titleLeadKey(cardTitleLead);
    const nodeLead = titleLeadKey(nodeTitle);
    if (lead.includes('white box') && nodeLead.includes('white box')) return true;
    if (lead.includes('black box') && nodeLead.includes('black box')) return true;
    if (lead.includes('deluxe') && nodeLead.includes('deluxe')) {
      if (lead.includes('black') && nodeLead.includes('black')) return true;
      if (lead.includes('white') && nodeLead.includes('white')) return true;
    }
    return false;
  };

  const nodeMatchesCardBox = (node, root) => {
    if (isLongStemShapeRoot(root)) return true;
    const cardBox = normField(root.dataset.boxColor || '');
    const nodeBox = nodeBoxValue(node);
    return boxColorMatchPdp(cardBox, nodeBox, node.title, root.dataset.titleLead || '');
  };

  const preferVariant = (a, b) => {
    if (!a) return b;
    if (!b) return a;
    const aAvail = a.availableForSale !== false;
    const bAvail = b.availableForSale !== false;
    if (aAvail !== bAvail) return aAvail ? a : b;
    const aHasImg = Boolean(a.featuredImage?.url || a.boxImage?.value || a.flowerImage?.value);
    const bHasImg = Boolean(b.featuredImage?.url || b.boxImage?.value || b.flowerImage?.value);
    if (aHasImg !== bHasImg) return aHasImg ? a : b;
    return a;
  };

  const preferBoxVariant = (a, b, colorRose) => {
    if (colorRose) {
      const aBox = Boolean(a?.boxImage?.value);
      const bBox = Boolean(b?.boxImage?.value);
      if (aBox !== bBox) return aBox ? a : b;
    }
    return preferVariant(a, b);
  };

  const getMasterOrder = (type) => {
    const order = window.TMR_SWATCH_MASTER_ORDER?.[type];
    return Array.isArray(order) ? order : [];
  };

  const inMasterOrder = (byKeyMap, type) =>
    getMasterOrder(type)
      .map((key) => byKeyMap.get(fieldExactKey(key)))
      .filter(Boolean);

  const buildFlowerVariantsForCard = (nodes, root) => {
    const cardBoxColor = normField(root.dataset.boxColor || '');
    const flowerByName = new Map();

    nodes
      .filter((n) => {
        if (!n?.flowerName?.value || !n?.boxColor?.value) return false;
        if (!nodeMatchesShapeTag(n, root)) return false;
        if (!nodeMatchesShapeFamily(n, root)) return false;
        if (!nodeMatchesEdition(n, root)) return false;
        if (isLongStemShapeRoot(root)) return true;
        return boxColorsExact(n.boxColor.value, cardBoxColor);
      })
      .forEach((n) => {
        const key = fieldExactKey(n.flowerName.value);
        flowerByName.set(key, preferVariant(flowerByName.get(key), n));
      });

    return inMasterOrder(flowerByName, 'rose');
  };

  const buildBoxSwatchVisual = (n, root, isBoxMode) => {
    const colorRose = isColorRoseSwatchRoot(root);
    const boxImg = normField(n?.boxImage?.value);
    if (isBoxMode && colorRose) {
      if (boxImg) {
        const safe = escapeAttr(boxImg);
        return {
          html: `<img class="tmr-card-swatch__img" src="${safe}" alt="" width="40" height="40" loading="eager" decoding="async">`,
          thumb: boxImg,
        };
      }
      const token = canonicalBoxColorKey(nodeBoxValue(n), n?.title) || 'default';
      return {
        html: `<span class="tmr-card-swatch__solid tmr-card-swatch__solid--${token}" aria-hidden="true"></span>`,
        thumb: '',
      };
    }
    const thumb = isBoxMode
      ? boxImg || n.flowerImage?.value || getMediaUrlsFromNode(n)[0] || n.featuredImage?.url || ''
      : n.flowerImage?.value || getMediaUrlsFromNode(n)[0] || n.featuredImage?.url || '';
    if (!thumb) return null;
    const safe = escapeAttr(thumb);
    return {
      html: `<img class="tmr-card-swatch__img" src="${safe}" alt="" width="40" height="40" loading="eager" decoding="async">`,
      thumb,
    };
  };

  const buildBoxVariantsForCard = (nodes, root) => {
    const cardFlowerName = normField(root.dataset.flowerName || '');
    const colorRose = isColorRoseSwatchRoot(root);
    const boxByColor = new Map();

    nodes
      .filter(
        (n) =>
          n &&
          nodeMatchesShapeTag(n, root) &&
          nodeMatchesShapeFamily(n, root) &&
          nodeMatchesEdition(n, root) &&
          n.boxColor?.value &&
          n.flowerName?.value &&
          flowerNamesExact(n.flowerName.value, cardFlowerName)
      )
      .forEach((n) => {
        const nodeBoxKey = fieldExactKey(n.boxColor.value);
        boxByColor.set(nodeBoxKey, preferBoxVariant(boxByColor.get(nodeBoxKey), n, colorRose));
      });

    return inMasterOrder(boxByColor, 'box');
  };

  // GraphQL returns price as a decimal-dollar string; the rest of this file works in
  // cents, matching the /products/{handle}.js REST endpoint used elsewhere here.
  const priceAmountToCents = (amount) => {
    const num = parseFloat(amount);
    return isNaN(num) ? null : Math.round(num * 100);
  };

  const renderApiSwatchRow = (root, variants) => {
    const row = root.querySelector('.tmr-card-swatches__row');
    if (!row) return;
    const handle = root.dataset.productHandle || '';
    const isBoxMode = isColorRoseSwatchRoot(root) || root.dataset.swatchMode === 'box';
    const html = variants
      .map((n) => {
        const mediaUrls = getMediaUrlsFromNode(n);
        const visual = buildBoxSwatchVisual(n, root, isBoxMode);
        if (!visual) return '';
        const preview = mediaUrls[0] || visual.thumb || '';
        const label = isBoxMode ? n.boxColor?.value || '' : n.flowerName?.value || '';
        const title = n.title || label;
        const avail = n.availableForSale !== false ? '1' : '0';
        const isSelected = n.handle === handle;
        const selected = isSelected ? ' is-selected' : '';
        const ariaCurrentAttr = isSelected ? ' aria-current="true"' : '';
        const sold = avail === '0' ? ' is-soldout' : '';
        const soldOutLabel = escapeAttr(root.dataset.soldOutLabel || 'Sold out');
        const soldBadge =
          avail === '0'
            ? `<span class="tmr-card-swatch__sold-badge badge badge--sold-out" aria-hidden="true">${soldOutLabel}</span>`
            : '';
        const safeThumb = escapeAttr(visual.thumb);
        const safePreview = escapeAttr(preview);
        const safeTitle = escapeAttr(title);
        const safeMedia = escapeAttr(mediaUrls.join(','));
        const soldAria = avail === '0' ? ' (Sold out)' : '';
        const priceCents = priceAmountToCents(n.priceRange?.minVariantPrice?.amount);
        const compareCents = priceAmountToCents(n.compareAtPriceRange?.minVariantPrice?.amount);
        const priceAttr = priceCents !== null ? ` data-price="${priceCents}"` : '';
        const compareAttr = compareCents !== null ? ` data-compare-price="${compareCents}"` : '';
        return `<button type="button" class="tmr-card-swatch${selected}${sold}" data-handle="${n.handle}" data-preview="${safePreview}" data-thumb="${safeThumb}" data-label="${label}" data-title="${safeTitle}" data-available="${avail}" data-media="${safeMedia}"${priceAttr}${compareAttr} aria-label="${label}${soldAria}"${ariaCurrentAttr}>${visual.html}${soldBadge}</button>`;
      })
      .join('');
    row.innerHTML = html;
    root.classList.remove('tmr-card-swatches--awaiting-api');
    root.dataset.tmrApiEnhanced = '1';
    markSwatchesReady(root);
    finalizeSwatchRoot(root);
  };

  let batchApiRunning = false;
  let batchApiQueued = false;

  const tryPaintPlpSwatchesFromWarmCache = (pending) => {
    if (!pending.length || !window.TMR_STOREFRONT_TOKEN) return false;
    const tags = collectShapeTagsForRoots(pending);
    if (!tags.size) return false;

    for (const tag of tags) {
      if (!tagNodesComplete(plpTagResolved[tag])) return false;
    }

    paintPlpSwatchRootsFromNodes(pending, new Map());
    return true;
  };

  const runBatchApiEnhance = async () => {
    if (batchApiRunning) {
      batchApiQueued = true;
      return;
    }

    const pending = getPendingPlpSwatchRoots();
    if (!pending.length) return;

    if (!window.TMR_STOREFRONT_TOKEN) {
      pending.forEach((root) => {
        root.dataset.tmrApiEnhanced = '1';
        finalizeSwatchRoot(root);
      });
      return;
    }

    batchApiRunning = true;
    try {
      const allTags = collectShapeTagsForRoots(pending);
      await Promise.all([...allTags].map((tag) => fetchProductsByShapeTag(tag)));
      paintPlpSwatchRootsFromNodes(pending, new Map());
    } finally {
      batchApiRunning = false;
      pending.forEach((root) => {
        if (root.dataset.tmrApiEnhanced !== '1') {
          root.dataset.tmrApiEnhanced = '1';
          finalizeSwatchRoot(root);
        }
      });
      if (batchApiQueued) {
        batchApiQueued = false;
        void runBatchApiEnhance();
      }
    }
  };

  window.__tmrRunPlpSwatchEnhance = runBatchApiEnhance;

  let plpSwatchBootstrapped = false;

  const initSwatches = async ({ facet = false } = {}) => {
    if (!facet && plpSwatchBootstrapped) return;
    if (!facet) plpSwatchBootstrapped = true;

    document.getElementById('tmr-plp-swatch-hold-style')?.remove();

    if (window.TMR_STOREFRONT_TOKEN) {
      const pending = getPendingPlpSwatchRoots();
      if (pending.length && !tryPaintPlpSwatchesFromWarmCache(pending)) {
        await runBatchApiEnhance();
      } else if (!pending.length) {
        await runBatchApiEnhance();
      }
    }
    bootCardSwatches();
  };

  window.__tmrPlpSwatchesScheduleInit = initSwatches;

  const scheduleInit = () => {
    void initSwatches({ facet: true });
  };

  if (window.matchMedia?.('(hover: hover)')?.matches) {
    document.addEventListener(
      'pointerenter',
      (e) => {
        const btn = e.target.closest?.('.tmr-card-swatch');
        if (!btn) return;
        const root = btn.closest('[data-tmr-card-swatches]');
        if (!root) return;
        warmSwatchUrls(btn);
      },
      { capture: true, passive: true }
    );
  }

  installPlpSwatchDelegates();

  void initSwatches();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => void initSwatches(), { once: true });
  }

  let facetMoTimer = null;
  const facetMo = new MutationObserver((mutations) => {
    let hasNew = false;
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (
          node.matches?.('[data-tmr-card-swatches]') ||
          node.querySelector?.('[data-tmr-card-swatches]')
        ) {
          hasNew = true;
          break;
        }
      }
      if (hasNew) break;
    }
    if (!hasNew) return;
    clearTimeout(facetMoTimer);
    facetMoTimer = setTimeout(scheduleInit, 150);
  });
  facetMo.observe(document.body, { childList: true, subtree: true });

  document.addEventListener('facet:update', () => {
    clearTimeout(facetMoTimer);
    facetMoTimer = setTimeout(scheduleInit, 300);
  });

  window.__tmrApplyPlpCardSwatch = applyVariantSelection;
})();
